import { CustomerHandle } from "resource-planner-model";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { AppState } from "../redux/AppTypes";

export class AggregatedCustomers {
  public items: { [id: string]: CustomerHandle } = {};

  add(customer: CustomerHandle): void {
    this.items[customer.id] = customer;
  }

  addAll(customers: CustomerHandle[]): void {
    customers.forEach((customer) => this.add(customer));
  }

  get(customerId: string): CustomerHandle {
    return this.items[customerId];
  }

  getAll(): CustomerHandle[] {
    return Object.values(this.items);
  }

  size(): number {
    return Object.keys(this.items).length;
  }
}

export const createCustomersSelector = () =>
  createSelector(
    (state: AppState) => state.customers.items,
    (_: AppState, year: number) => year,
    (_: AppState, _year: number, month: number) => month,
    (items, year, month) =>
      Object.values(items).reduce((all, customer) => {
        const handle = new CustomerHandle(customer);
        const budget = handle.getBudget(year, month);
        if (budget.emHours + budget.teamHours + budget.opsHours > 0) {
          all.add(handle);
        }
        return all;
      }, new AggregatedCustomers())
  );

export default function useCustomers(
  year: number,
  month: number
): AggregatedCustomers {
  const selector = useMemo(createCustomersSelector, []);
  return useSelector((state: AppState) =>
    selector(state, year, month)
  );
}
